import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { SigninRequest } from '@interfaces/auth.interface';
import {
  UpdateCurrentUserRequest,
  UserWithPermissions,
} from '@interfaces/users.interface';

import { useLogoutMutation, useSigninMutation } from '@api/auth.api';
import api from '@api/index';
import { useUpdateCurrentUserMutation } from '@api/users.api';

import {
  logout as logoutAction,
  selectCurrentUser,
  setCredentials,
} from '@slices/auth.slice';

const useAuth = () => {
  const dispatch = useDispatch();

  const user = useSelector(selectCurrentUser);

  const [signinMutation, { isLoading: isLoadingSignin }] = useSigninMutation();
  const [logoutMutation, { isLoading: isLoadingLogout }] = useLogoutMutation();
  const [patchMutation, { isLoading: isLoadingPatch }] =
    useUpdateCurrentUserMutation();

  const signin = useCallback(
    async (data: SigninRequest): Promise<UserWithPermissions> => {
      const user = await signinMutation(data).unwrap();

      dispatch(setCredentials(user));

      return user;
    },
    [dispatch, signinMutation],
  );

  const logout = useCallback(async () => {
    await logoutMutation().unwrap();

    dispatch(api.util.resetApiState());
    dispatch(logoutAction());
  }, [dispatch, logoutMutation]);

  const patch = useCallback(
    async (body: UpdateCurrentUserRequest) => {
      const patchedUser = await patchMutation(body).unwrap();
      const updatedUser = {
        ...user,
        ...patchedUser,
      };
      dispatch(setCredentials(updatedUser));
    },
    [dispatch, patchMutation, user],
  );

  return useMemo(
    () => ({
      user,
      isAuthenticated: user !== null,
      signin,
      patch,
      logout,
      isLoading: isLoadingSignin || isLoadingPatch || isLoadingLogout,
    }),
    [
      user,
      signin,
      patch,
      logout,
      isLoadingSignin,
      isLoadingPatch,
      isLoadingLogout,
    ],
  );
};

export default useAuth;
